import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MessageSquareIcon } from 'lucide-react';
import {
  CardHeader,
  EmptyState,
  ErrorNotice,
  LoadingState,
  MessageThread,
  Panel,
  Tabs,
  type MentionCandidate,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { listInternalMessages, listMentionCandidates, postInternalMessage } from '@/lib/internal-messages';
import type { NewMessage } from '@/lib/workspace';
import { ClientMessagesThread } from './event-client-portal';

/**
 * The event's two threads: BCE talking to BCE, and the thread the client
 * sees on their portal. The same chat for both; only the audience differs.
 */

const THREADS = ['internal', 'client'] as const;
type Thread = (typeof THREADS)[number];

const THREAD_ITEMS: readonly { readonly key: Thread; readonly label: string }[] = [
  { key: 'internal', label: 'Internal' },
  { key: 'client', label: 'Client' },
];

/** How often the thread re-reads. The client's portal streams; here polling is enough. */
const MESSAGE_POLL_MS = 10_000;

function asThread(value: string | null): Thread {
  return (THREADS as readonly string[]).includes(value ?? '') ? (value as Thread) : 'internal';
}

export interface MessagesTabProps {
  readonly eventId: string;
  /** The client is on the portal, so their thread exists. */
  readonly clientPortal: boolean;
}

export function MessagesTab({ eventId, clientPortal }: MessagesTabProps): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const thread = asThread(params.get('thread'));

  return (
    <div className="mt-6 space-y-5">
      <div data-guide="messages-threads"><Tabs<Thread>
        label="Threads"
        items={THREAD_ITEMS}
        active={thread}
        onChange={(next) =>
          setParams(
            (previous) => {
              const nextParams = new URLSearchParams(previous);
              nextParams.set('thread', next);
              return nextParams;
            },
            { replace: true },
          )
        }
      /></div>
      {thread === 'internal' ? <InternalThread eventId={eventId} /> : null}
      {thread === 'client' ? (
        clientPortal ? (
          <ClientMessagesThread eventId={eventId} />
        ) : (
          <Panel>
            <EmptyState
              icon={<MessageSquareIcon aria-hidden className="size-6" />}
              title="This client has not opened their portal yet"
              message="Their thread starts once they are on a client portal: their organization's enterprise portal, or the default client portal."
            />
          </Panel>
        )
      ) : null}
    </div>
  );
}

function InternalThread({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  // Anyone who can see the event can be tagged: every internal user, plus the
  // contractors assigned to it. The API decides, so the list and the check agree.
  const candidatesQuery = useQuery({
    queryKey: ['mention-candidates', eventId],
    queryFn: ({ signal }) => listMentionCandidates(eventId, signal),
    staleTime: 5 * 60_000,
  });
  const messages = useQuery({
    queryKey: ['internal-messages', eventId],
    queryFn: ({ signal }) => listInternalMessages(eventId, signal),
    refetchInterval: MESSAGE_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const post = useMutation({
    mutationFn: (message: NewMessage) => postInternalMessage(eventId, message),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['internal-messages', eventId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (messages.isPending) return <LoadingState label="Loading the thread…" />;
  if (messages.isError) return <ErrorNotice title="Thread unavailable" message={errorMessage(messages.error)} />;

  const candidates: MentionCandidate[] = (candidatesQuery.data ?? []).map((person) => ({ id: person.id, label: person.name }));

  return (
    <Panel className="overflow-hidden">
      <div data-guide="messages-internal" className="p-5 pb-4">
        <CardHeader
          className="mb-0"
          title="Internal"
          description="BCE only. The client never sees this thread; tag anyone who can see this event and they are told."
        />
      </div>
      <div data-guide="messages-thread"><MessageThread
        messages={messages.data}
        mentionCandidates={candidates}
        onPost={(message) => post.mutate(message)}
        isPosting={post.isPending}
        placeholder="Message the BCE team. Type @ to tag someone"
        empty={{ title: 'Nothing here yet', message: 'Notes for the team on this event, out of the client’s sight. Tag someone and they are told.' }}
      /></div>
    </Panel>
  );
}
